import { useId, useRef, useState, type ReactNode } from 'react'
import { Mtds } from 'prove-common'
import { ReferenceDialog } from './ReferenceDialog'
import {
  referenceErrorMessage,
  requireSuccessfulResponse
} from './referenceErrors'

export type ReferenceDeleteDialogProps = {
  id?: string
  className?: string
  name: string
  description?: ReactNode
  successMessage?: string
  onClose: () => void
  onDelete: () => Promise<unknown>
  onDeleted?: () => void | Promise<void>
}

export function ReferenceDeleteDialog({
  id,
  className,
  name,
  description = 'Oppføringen vil bli permanent slettet. Handlingen kan ikke angres.',
  successMessage = 'Oppføringen er slettet',
  onClose,
  onDelete,
  onDeleted
}: ReferenceDeleteDialogProps) {
  const generatedId = useId()
  const dialogId = id ?? generatedId
  const titleId = `${dialogId}-title`
  const [pending, setPending] = useState(false)
  const [failure, setFailure] = useState('')
  const submitting = useRef(false)

  const handleClose = () => {
    if (!submitting.current) onClose()
  }

  const handleDelete = async () => {
    if (submitting.current) return
    submitting.current = true
    setPending(true)
    setFailure('')
    try {
      requireSuccessfulResponse(await onDelete())
      Mtds.toast.success(successMessage)
      onClose()
      await onDeleted?.()
    } catch (error) {
      setFailure(referenceErrorMessage(error, true))
    } finally {
      submitting.current = false
      setPending(false)
    }
  }

  return (
    <ReferenceDialog
      className={className}
      id={dialogId}
      open
      aria-labelledby={titleId}
      closedby='closerequest'
      onClose={handleClose}
      onCancel={(event) => {
        event.preventDefault()
        handleClose()
      }}
    >
      <Mtds.Button
        type='button'
        command='request-close'
        commandfor={dialogId}
        aria-label='Lukk'
        disabled={pending}
        onClick={handleClose}
      />
      <Mtds.Grid data-gap='5'>
        <Mtds.Heading as='h2' data-size='md' id={titleId}>
          Slett {name}?
        </Mtds.Heading>
        {typeof description === 'string' ? <p>{description}</p> : description}
        {failure && (
          <Mtds.Alert as='div' role='alert' data-color='danger'>
            {failure}
          </Mtds.Alert>
        )}
        <Mtds.Flex data-justify='end'>
          <Mtds.Button disabled={pending} onClick={handleClose}>
            Avbryt
          </Mtds.Button>
          <Mtds.Button
            data-variant='primary'
            data-color='danger'
            disabled={pending}
            aria-busy={pending}
            onClick={handleDelete}
          >
            Slett
          </Mtds.Button>
        </Mtds.Flex>
      </Mtds.Grid>
    </ReferenceDialog>
  )
}
